import { useCallback, useMemo } from "react";

import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import useWindowSize from "../../../hooks/useWindowSize";
import useGetLocalePath from "../../../hooks/useGetLocalePath";

import {
  setBlur,
  setHeaderMenuOpen,
  setShowContactDialog,
  setShowNewsletterRegisterDialog,
} from "../../../redux/reducers/uiReducer";

import LangSwitch from "../../lang-switch";

import { logDebug, logError } from "../../../utils/logger";

const FOOTER_ITEM = {
  YDRAY: "ydray",
  EFFICY: "efficy",
  PRESS: "press",
  CONTACT: "contact",
  NEWSLETTER: "newsletter",
  FAQ: "faq",
  PRIVACY_POLICY: "privacy_policy",
  COOKIE_POLICY: "cookie_policy",
  CONSENT_COOKIES: "consent_cookies",
  LEGAL_WARN: "legal_warn",
  SEND_FILES: "send_files",
  SHARE_FILES: "share_files",
  YOUTUBE: "youtube",
  LINKEDIN: "linkedin",
  TWITTER: "twitter",
  FACEBOOK: "facebook",
  INSTAGRAM: "instagram",
};

function LandingFooter() {
  const { t } = useTranslation();

  const windowSize = useWindowSize();
  const { width } = windowSize;

  const dispatch = useDispatch();

  const { getLocalePath } = useGetLocalePath();

  const ydrayLogo = useMemo(() => {
    return width >= 1200
      ? "../../../assets/images/ydray-icon.svg"
      : "../../../assets/images/ydray-icon-plain-black.svg";
  }, [width]);

  const redirectGetLocalePath = useCallback(
    (path) => {
      return `${process.env.REACT_APP_PUBLIC_URL}${getLocalePath(path)}`;
    },
    [getLocalePath]
  );

  const openConsentCookiesHandler = useCallback((evt) => {
    logDebug("LandingFooter", "openConsentCookiesHandler", {
      evt: evt,
    });
    try {
      window.googlefc.callbackQueue.push({
        CONSENT_DATA_READY: () => window.googlefc.showRevocationMessage(),
      });
    } catch (error) {
      logError("LandingFooter", "openConsentCookiesHandler", { error: error });
    }
  }, []);

  const onClickItemColumnHandler = useCallback(
    (evt, item) => {
      logDebug("LandingFooter", "onClickItemColumnHandler", {
        evt: evt,
        item: item,
      });

      evt.preventDefault();
      evt.stopPropagation();
      dispatch(setHeaderMenuOpen(false));

      switch (item) {
        case FOOTER_ITEM.YDRAY:
          window.location.href = redirectGetLocalePath(t("routes.common.home"));
          break;
        case FOOTER_ITEM.PRESS:
          window.location.href = redirectGetLocalePath(
            t("routes.public.press")
          );
          break;
        case FOOTER_ITEM.CONTACT:
          dispatch(setBlur(true));
          dispatch(setShowContactDialog(true));
          break;
        case FOOTER_ITEM.NEWSLETTER:
          dispatch(setBlur(true));
          dispatch(setShowNewsletterRegisterDialog(true));
          break;
        case FOOTER_ITEM.FAQ:
          window.location.href = redirectGetLocalePath(t("routes.public.faq"));
          break;
        case FOOTER_ITEM.PRIVACY_POLICY:
          window.location.href = redirectGetLocalePath(
            t("routes.public.privacy")
          );
          break;
        case FOOTER_ITEM.COOKIE_POLICY:
          window.location.href = redirectGetLocalePath(
            t("routes.public.cookies")
          );
          break;
        case FOOTER_ITEM.CONSENT_COOKIES:
          openConsentCookiesHandler(evt);
          break;
        case FOOTER_ITEM.LEGAL_WARN:
          window.location.href = redirectGetLocalePath(
            t("routes.public.legal")
          );
          break;
        case FOOTER_ITEM.SEND_FILES:
          window.location.href = redirectGetLocalePath(
            t("routes.public.send_files.index")
          );
          break;
        case FOOTER_ITEM.SHARE_FILES:
          window.location.href = redirectGetLocalePath(
            t("routes.public.share_files.index")
          );
          break;
        case FOOTER_ITEM.YOUTUBE:
          window.open(
            t("social_media.links.youtube"),
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case FOOTER_ITEM.LINKEDIN:
          window.open(
            t("social_media.links.linkedin"),
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case FOOTER_ITEM.TWITTER:
          window.open(
            t("social_media.links.twitter"),
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case FOOTER_ITEM.FACEBOOK:
          window.open(
            t("social_media.links.facebook"),
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case FOOTER_ITEM.INSTAGRAM:
          window.open(
            t("social_media.links.instagram"),
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case FOOTER_ITEM.EFFICY:
          window.open(
            process.env.REACT_APP_EFFICY_REFERAL_LINK,
            "_blank",
            "noopener,noreferrer"
          );
          break;
        default:
          logError("LandingFooter", "onClickItemColumnHandler", {
            evt: evt,
            item: item,
          });
          break;
      }
    },
    [t, dispatch, redirectGetLocalePath, openConsentCookiesHandler]
  );

  return (
    <div className="landing__footer">
      <div className="landing__footer__container">
        <div className="landing__footer__content">
          <div className="landing__footer__logos">
            <Link
              className="footer__logo ydray"
              to={redirectGetLocalePath(t("routes.common.home"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.YDRAY)
              }
            >
              <img
                className="logo"
                src={ydrayLogo}
                title={t("logo.title")}
                alt={t("logo.alt")}
                height="auto"
                width="auto"
                loading="lazy"
              />
              <img
                className="wordmark"
                src="../../../assets/images/ydray-wordmark-black.svg"
                title={t("wordmark.title")}
                alt={t("wordmark.alt")}
                height="auto"
                width="auto"
                loading="lazy"
              />
            </Link>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              className="footer__logo efficy"
              to={process.env.REACT_APP_EFFICY_REFERAL_LINK}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.EFFICY)
              }
            >
              <img
                className="logo"
                src="../../../assets/images/efficy-logo-elite.png"
                title={t("logo.efficy.title")}
                alt={t("logo.efficy.alt")}
                height="auto"
                width="auto"
                loading="lazy"
              />
            </Link>
          </div>
          <div className="landing__footer__socials">
            <div className="landing__footer__socials__container">
              <Link
                className="footer__social-item"
                target="_blank"
                rel="noopener noreferrer"
                title={t("social_media.youtube")}
                to={t("social_media.links.youtube")}
                onClick={(env) =>
                  onClickItemColumnHandler(env, FOOTER_ITEM.YOUTUBE)
                }
              >
                <span className="footer__social-item__text">
                  {t("social_media.youtube")}
                </span>
                <span className="footer__social-item__icon icon-youtube"></span>
              </Link>
              <Link
                className="footer__social-item"
                target="_blank"
                rel="noopener noreferrer"
                title={t("social_media.linkedin")}
                to={t("social_media.links.linkedin")}
                onClick={(env) =>
                  onClickItemColumnHandler(env, FOOTER_ITEM.LINKEDIN)
                }
              >
                <span className="footer__social-item__text">
                  {t("social_media.linkedin")}
                </span>
                <span className="footer__social-item__icon icon-linkedin"></span>
              </Link>
              <Link
                className="footer__social-item"
                target="_blank"
                rel="noopener noreferrer"
                title={t("social_media.twitter")}
                to={t("social_media.links.twitter")}
                onClick={(evt) =>
                  onClickItemColumnHandler(evt, FOOTER_ITEM.TWITTER)
                }
              >
                <span className="footer__social-item__text">
                  {t("social_media.twitter")}
                </span>
                <span className="footer__social-item__icon icon-twitter-x"></span>
              </Link>
              <Link
                className="footer__social-item"
                target="_blank"
                rel="noopener noreferrer"
                title={t("social_media.facebook")}
                href={t("social_media.links.facebook")}
                onClick={(evt) =>
                  onClickItemColumnHandler(evt, FOOTER_ITEM.FACEBOOK)
                }
              >
                <span className="footer__social-item__text">
                  {t("social_media.facebook")}
                </span>
                <span className="footer__social-item__icon icon-facebook"></span>
              </Link>
              <Link
                className="footer__social-item"
                href={t("social_media.links.instagram")}
                target="_blank"
                rel="noopener noreferrer"
                title={t("social_media.instagram")}
                onClick={(evt) =>
                  onClickItemColumnHandler(evt, FOOTER_ITEM.INSTAGRAM)
                }
              >
                <span className="footer__social-item__text">
                  {t("social_media.instagram")}
                </span>
                <span className="footer__social-item__icon icon-instagram"></span>
              </Link>
            </div>
            <LangSwitch />
          </div>
          <div className="landing__footer__links">
            <Link
              className="footer__link-item"
              title={t("landing.footer.press")}
              to={redirectGetLocalePath(t("routes.public.press"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.PRESS)
              }
            >
              {t("landing.footer.press")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.contact")}
              to={redirectGetLocalePath(t("routes.public.contact"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.CONTACT)
              }
            >
              {t("landing.footer.contact")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.newsletter")}
              to={redirectGetLocalePath(t("routes.public.newsletter_reg"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.NEWSLETTER)
              }
            >
              {t("landing.footer.newsletter")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.faq")}
              to={redirectGetLocalePath(t("routes.public.faq"))}
              onClick={(evt) => onClickItemColumnHandler(evt, FOOTER_ITEM.FAQ)}
            >
              {t("landing.footer.faq")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.privacy_policy")}
              to={redirectGetLocalePath(t("routes.public.privacy"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.PRIVACY_POLICY)
              }
            >
              {t("landing.footer.privacy_policy")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.cookie_policy")}
              to={redirectGetLocalePath(t("routes.public.cookies"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.COOKIE_POLICY)
              }
            >
              {t("landing.footer.cookie_policy")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.consent_cookies")}
              to={""}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.CONSENT_COOKIES)
              }
            >
              {t("landing.footer.consent_cookies")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.legal_warn")}
              to={redirectGetLocalePath(t("routes.public.legal"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.LEGAL_WARN)
              }
            >
              {t("landing.footer.legal_warn")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.send_files")}
              to={redirectGetLocalePath(t("routes.public.send_files.index"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.SEND_FILES)
              }
            >
              {t("landing.footer.send_files")}
            </Link>
            <Link
              className="footer__link-item"
              title={t("landing.footer.share_files")}
              to={redirectGetLocalePath(t("routes.public.share_files.index"))}
              onClick={(evt) =>
                onClickItemColumnHandler(evt, FOOTER_ITEM.SHARE_FILES)
              }
            >
              {t("landing.footer.share_files")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingFooter;
